import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Collapse,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { kollalogowhite } from '../assets';
import './Header.css';

interface HeaderProps {
  selectedTab: string;
  onTabChange: (route: string) => void;
}

const Header: React.FC<HeaderProps> = ({ selectedTab, onTabChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // Helper: checks if current path is blogs or blog details
  const isBlogsActive = React.useMemo(() => {
    return location.pathname === "/blogs" || location.pathname.startsWith("/blogs/");
  }, [location.pathname]);

  // Ensure selectedTab is valid and update for blog details
  useEffect(() => {
    // If on blog details, set tab as '/blogs'
    if (isBlogsActive && selectedTab !== "/blogs") {
      onTabChange("/blogs");
    } else if (
      !isBlogsActive &&
      !tabLabels.concat(tableLabels2).some((tab) => tab.route === selectedTab)
    ) {
      onTabChange(location.pathname);
    }
    // eslint-disable-next-line
  }, [location.pathname, selectedTab, onTabChange, isBlogsActive]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    onTabChange(newValue);
    navigate(newValue);
    setDrawerOpen(false);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDropdownMouseEnter = () => {
    setDropdownVisible(true);
  };

  const handleDropdownMouseLeave = () => {
    setDropdownVisible(false);
  };

  const handleNavigate = (route: string) => {
    window.open(route, '_blank');
    setDropdownVisible(false);
    setDrawerOpen(false);
  };

  const tabLabels = [
    { label: 'Home', route: '/' },
  ];

  const tableLabels2 = [
    { label: 'Blog', route: '/blogs' },
    { label: 'Contact Us', route: '/contactus' },
  ];

  const tabButtonStyles = {
    fontSize: '16px',
    color: '#FFFFFF99', 
    '&:hover': {
      color: '#FFFFFF', 
      fontWeight: '600',
    },
  };

  const drawerItemStyles = {
    fontSize: '16px',
    color: '#FFFFFF99', 
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: '#FFFFFF',
    },
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%)',
          top: 0,
          minHeight: 0,
          zIndex: 11,
          boxShadow: 'none',
          backdropFilter: 'none',
          pointerEvents: 'auto',
          // backgroundColor: 'rgba(0, 0, 0, 0)',
          borderRadius: 0,
  
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', ml: { xs: 2, md: 4 }, cursor: 'pointer' }}
            onClick={() => handleTabChange(null as any, '/')}
          >
            <img src={kollalogowhite} alt="Best apartments in Hyderabad by Kolla Group" style={{ width: 100, userSelect: 'none' }} />
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              flexGrow: 1,
              justifyContent: 'flex-end',
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                '& .MuiTab-root': {
                  ...tabButtonStyles,
                  minWidth: 'auto',
                },
                '& .Mui-selected': {
                  color: 'primary.main', 
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: '3.5px', 
                },
              }}
            >
                {tabLabels.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  value={tab.route}
                  sx={{
                  color: '#FFFFFF', 
                  fontWeight: location.pathname.startsWith(tab.route) ? 'bold' : 'normal',
                  '&.MuiTab-root': { color: '#FFFFFF', fontWeight: '500' },
                  '&.MuiTab-root:hover': { fontWeight: '600' }, 
                  '&.Mui-selected': { color: 'primary.main', fontWeight: '600'  }, 
                  '&.Mui-selected:hover': { color: 'primary.main' },
                  }}
                />
                ))}
            </Tabs>
            <div
              className="menu"
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <button 
                className="tab-button"
                style={{
                  ...tabButtonStyles,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: '500',
                }}
              >
                Projects <ArrowDropDownIcon />
              </button>
              {dropdownVisible && (
                <div className="dropdown-menu">
                  <ul>
                    <li onClick={() => handleNavigate('/luxor-exclusive')}>
                      Luxor Exclusive
                    </li>
                    <li onClick={() => handleNavigate('/luxuria')}>
                      Luxuria
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <Tabs
              value={isBlogsActive ? "/blogs" : selectedTab}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                '& .MuiTab-root': {
                  ...tabButtonStyles,
                  minWidth: 'auto',
                },
                '& .Mui-selected': {
                  color: 'primary.main', 
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: '3.5px', 
                },
              }}
            >
              {tableLabels2.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  value={tab.route}
                  sx={{
                    color: '#FFFFFF99', 
                    fontWeight: (tab.route === "/blogs" && isBlogsActive) || location.pathname.startsWith(tab.route) ? 'bold' : 'normal',
                    '&.MuiTab-root': { color: '#FFFFFF', fontWeight: '500' },
                    '&.MuiTab-root:hover': { fontWeight: '600' }, 
                    '&.Mui-selected': { color: 'primary.main', fontWeight: '600'  }, 
                    '&.Mui-selected:hover': { color: 'primary.main' },
                  }}
                />
              ))}
            </Tabs>
            {/* <AnimatedButton onClick={handleClickOpen}>
              Download Brochure
            </AnimatedButton> */}
          </Box>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: 'block', md: 'none' }, color: '#FFFFFF' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
        width: 250,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: '#FFFFFF',
        border: 'none',
        transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
        transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
        opacity: drawerOpen ? 1 : 0,
          },
        }}
      >
        <Box sx={{ textAlign: 'right', p: 2 }}>
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#FFFFFF' }}>
        <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {tabLabels.map((tab, index) => (
        <ListItem
          key={index}
          onClick={() => {
            handleNavigate(tab.route);
            setDrawerOpen(false);
          }}
          sx={{
            '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
            animation: `fadeIn 0.3s ease-in-out ${index * 0.1}s forwards`,
            opacity: 0,
            '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateX(20px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          <ListItemText
            primary={tab.label}
            sx={{
          ...drawerItemStyles,
          textDecoration:
            location.pathname === tab.route ? 'underline' : 'none',
          color:
            location.pathname === tab.route
              ? 'primary.main'
              : '#FFFFFF99',
            }}
          />
        </ListItem>
          ))}
          <ListItem
        onClick={() => setDropdownVisible(!dropdownVisible)}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
          animation: `fadeIn 0.3s ease-in-out ${tabLabels.length * 0.1}s forwards`,
          opacity: 0,
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateX(20px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
        }}
          >
        <ListItemText
          primary="Projects"
          sx={{
            ...drawerItemStyles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        />
        <ArrowDropDownIcon
          sx={{
            transform: dropdownVisible ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
          </ListItem>
          <Collapse in={dropdownVisible} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                sx={{
                  pl: 4,
                  animation: `fadeIn 0.3s ease-in-out ${tabLabels.length * 0.1 + 0.1}s forwards`,
                  opacity: 0,
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateX(20px)' },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
                onClick={() => {
                  handleNavigate('/luxor-exclusive');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="Luxor Exclusive" sx={drawerItemStyles} />
              </ListItem>
              <ListItem
                sx={{
                  pl: 4,
                  animation: `fadeIn 0.3s ease-in-out ${tabLabels.length * 0.1 + 0.2}s forwards`,
                  opacity: 0,
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateX(20px)' },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
                onClick={() => {
                  handleNavigate('/luxuria');
                  setDrawerOpen(false);
                }}
              >
                <ListItemText primary="Luxuria" sx={drawerItemStyles} />
              </ListItem>
            </List>
          </Collapse>
          {tableLabels2.map((tab, index) => (
            <ListItem
              key={index}
              onClick={() => {
                handleNavigate(tab.route);
                setDrawerOpen(false);
              }}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                },
                animation: `fadeIn 0.3s ease-in-out ${(tabLabels.length + index) * 0.1}s forwards`,
                opacity: 0,
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateX(20px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <ListItemText
                primary={tab.label}
                sx={{
                  ...drawerItemStyles,
                  textDecoration:
                    ((tab.route === "/blogs" && isBlogsActive) || location.pathname === tab.route)
                      ? 'underline'
                      : 'none',
                  color:
                    ((tab.route === "/blogs" && isBlogsActive) || location.pathname === tab.route)
                      ? 'primary.main'
                      : '#FFFFFF99',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* <ModalForm open={open} handleClose={handleClose} /> */}
    </>
  );
};

export default Header;


